import React from "react";
import {Box} from "@mui/material";
import type {GridColDef, useGridApiRef} from "@mui/x-data-grid-premium";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid.tsx";
import type {PtExclusions, PtTier} from "../paymentTermsUtils.ts";
import {TIER_LABEL, TIER_ORDER, tierOf} from "../paymentTermsUtils.ts";
import {formatTableMoney} from "../paymentTermsDisplay.ts";
import {ReviewChip, TermCountCell, TermGaugeCell, TierPill, WcOpportunityCell} from "./PaymentTermsCells.tsx";
import type {PtSupplier} from "~/experiments/pages/payment-terms/PaymentTermsClasses.ts";

export type PaymentTermsGridApiRef = ReturnType<typeof useGridApiRef>;

type Props = {
    rows: PtSupplier[];
    apiRef: PaymentTermsGridApiRef;
    exclusions: PtExclusions;
    /** Null on live bags until currency is served by the API. */
    currency: string | null;
    loading?: boolean;
    onRowClick: (row: PtSupplier) => void;
};

const TIER_OPTIONS = (Object.keys(TIER_ORDER) as PtTier[])
    .map(value => ({value, label: TIER_LABEL[value]}));

const REVIEW_OPTIONS = [
    {value: 'potential', label: 'Potential'},
    {value: 'excluded', label: 'Excluded'},
];

export const PaymentTermsTable: React.FC<Props> = ({rows, apiRef, exclusions, currency, loading = false, onRowClick}) => {
    const columns = React.useMemo<GridColDef<PtSupplier>[]>(() => [
        {
            field: 'supplier',
            headerName: 'Parent supplier',
            flex: 1.7,
            minWidth: 230,
            renderCell: ({row}) => <span className="pt-supplier-name">{row.supplier}</span>,
        },
        {
            field: 'tier',
            headerName: 'Opportunity type',
            type: 'singleSelect',
            valueOptions: TIER_OPTIONS,
            flex: 1,
            minWidth: 165,
            valueGetter: (_value, row) => tierOf(row),
            sortComparator: (a, b) => TIER_ORDER[a as PtTier] - TIER_ORDER[b as PtTier],
            renderCell: ({row}) => <TierPill tier={tierOf(row)}/>,
        },
        {
            field: 'spend',
            headerName: 'Total spend (period)',
            type: 'number',
            flex: 0.85,
            minWidth: 140,
            valueFormatter: (value: number) => formatTableMoney(value, currency),
        },
        {
            field: 'currentTerm',
            headerName: 'Weighted avg term vs target',
            description: 'Spend-weighted average payment term compared with the policy target',
            type: 'number',
            flex: 1.25,
            minWidth: 195,
            align: 'left',
            headerAlign: 'left',
            renderCell: ({row}) => <TermGaugeCell row={row}/>,
        },
        {
            field: 'gapDays',
            headerName: 'Term gap (days)',
            type: 'number',
            flex: 0.65,
            minWidth: 120,
            valueFormatter: (value: number) => value > 0 ? `${value.toFixed(0)}d` : '—',
        },
        {
            field: 'distinctTermCount',
            headerName: 'Terms in use',
            type: 'number',
            flex: 0.6,
            minWidth: 110,
            renderCell: ({row}) => <TermCountCell row={row}/>,
        },
        {
            field: 'invoiceCount',
            headerName: 'Invoice count',
            type: 'number',
            flex: 0.6,
            minWidth: 110,
        },
        {
            field: 'quickWinOpportunity',
            headerName: 'Quick win (best term)',
            description: 'Cash release from aligning every entity to the best term this supplier already accepts',
            type: 'number',
            flex: 0.85,
            minWidth: 150,
            valueFormatter: (value: number) => value > 0 ? formatTableMoney(value, currency) : '—',
        },
        {
            field: 'wcOpportunity',
            headerName: 'Working capital opportunity',
            description: 'Cash release from extending this supplier to the policy target term',
            type: 'number',
            flex: 1,
            minWidth: 170,
            renderCell: ({row}) => (
                <WcOpportunityCell
                    value={row.wcOpportunity}
                    currency={currency}
                    excluded={!!exclusions[row.id]}
                />
            ),
        },
        {
            field: 'review',
            headerName: 'Review',
            type: 'singleSelect',
            valueOptions: REVIEW_OPTIONS,
            flex: 0.6,
            minWidth: 110,
            valueGetter: (_value, row) => exclusions[row.id] ? 'excluded' : 'potential',
            renderCell: ({row}) => <ReviewChip excluded={!!exclusions[row.id]} reason={exclusions[row.id]}/>,
        },
    ], [exclusions, currency]);

    return (
        <Box sx={{width: '100%'}}>
            <CustomDataGrid
                className="base-table-muix auto-row-height-centered"
                apiRef={apiRef}
                rows={rows}
                columns={columns}
                loading={loading}
                getRowId={row => row.id}
                autoRowHeight
                getRowClassName={({row}) => exclusions[row.id] ? 'pt-row-excluded' : ''}
                headerFilters
                disableRowSelectionOnClick
                disableAggregation
                disableRowGrouping
                pagination
                pageSizeOptions={[50, 100, 200]}
                onRowClick={({row}) => onRowClick(row)}
                initialState={{
                    pagination: {paginationModel: {pageSize: 50, page: 0}},
                    sorting: {sortModel: [{field: 'wcOpportunity', sort: 'desc'}]},
                }}
                sx={{
                    border: 'none',
                    '--rowBorderColor': 'rgba(0,0,0,0.08)',
                    '& .MuiDataGrid-columnHeaderTitle': {fontWeight: 700, whiteSpace: 'normal', lineHeight: 1.2},
                    '& .MuiDataGrid-row': {cursor: 'pointer'},
                }}
            />
        </Box>
    );
};
